import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Swipeable from 'react-swipeable';
import { Base } from '@fiverr-private/modal/src';
import Slideshow from '@fiverr-private/slideshow/src/Slideshow';
import { GIG_GALLERY } from '../../../../utils/events';
import { keyCode } from '../constants';
import * as Slides from '../slides';

import './index.scss';

// The number of images ahead of the current slider to preload...
const LOAD_AHEAD = 1;

class GalleryModal extends PureComponent {

    constructor(props) {
        super(props);

        // Refs
        this.slideshowWrapper = null;

        // Instance vars
        this.openModal = () => null;
        this.closeModal = () => null;
        this.isOpen = false;

        // Bindings
        this.open = this.open.bind(this);
        this.changeSlide = this.changeSlide.bind(this);
        this.handleOpen = this.handleOpen.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handlePrev = this.handlePrev.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleSwipeLeft = this.handleSwipeLeft.bind(this);
        this.handleSwipeRight = this.handleSwipeRight.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleSlideClick = this.handleSlideClick.bind(this);
        this.getModalHandles = this.getModalHandles.bind(this);
        this.renderSlide = this.renderSlide.bind(this);
        this.renderNavigation = this.renderNavigation.bind(this);
        this.renderContent = this.renderContent.bind(this);

        // Initial state
        this.state = {
            currSlideIndex: props.selectedIndex,
            slideZoomingIndex: null
        };
    }

    componentWillReceiveProps(nextProps) {
        this.changeSlide(nextProps.selectedIndex);
    }

    open() {
        this.openModal();
    }

    handleOpen() {
        if (window && window.document) {
            // Prevent scrolling on window
            window.document.body.classList.add('no-scroll');
        }

        // Give focus to the component to allow key events
        this.slideshowWrapper && this.slideshowWrapper.focus({preventScroll: true});
    }

    changeSlide(index, e, callback) {
        e && e.preventDefault && e.preventDefault();
        const { length : len } = this.props.slides,
            currSlideIndex = this.state;
        let nextSlide = index;

        // No changes, exit early...
        if (currSlideIndex === index) { return; }

        if (nextSlide < 0) {
            nextSlide = len - 1;
        }
        if (nextSlide >= len || typeof index !== 'number') {
            nextSlide = 0;
        }

        this.setState({currSlideIndex: nextSlide}, callback);

        if (this.isOpen) {
            this.slideshowWrapper && this.slideshowWrapper.focus({preventScroll: true});
        }
    }

    handlePrev(e) {
        const { biEvents } = this.context;
        e && e.preventDefault && e.preventDefault();
        this.changeSlide(this.state.currSlideIndex - 1, null, () => {
            biEvents.sendBigQueryEvent({
                eventName: GIG_GALLERY,
                params: { type:'clicked_previous', source: 'zoom_popup' }
            });
        });
    }

    handleNext(e) {
        const { biEvents } = this.context;
        e && e.preventDefault && e.preventDefault();
        this.changeSlide(this.state.currSlideIndex + 1, null, () => {
            biEvents.sendBigQueryEvent({
                eventName: GIG_GALLERY,
                params: { type:'clicked_next', source: 'zoom_popup' }
            });
        });

    }

    handleSwipeLeft(e) {
        if (this.state.currSlideIndex !== this.state.slideZoomingIndex) {
            this.handleNext(e);
        }
    }

    handleSwipeRight(e) {
        if (this.state.currSlideIndex !== this.state.slideZoomingIndex) {
            this.handlePrev(e);
        }
    }

    handleKeyDown(e) {
        if (!e) { return; }
        (e.keyCode === keyCode.ARROW_LEFT) && this.handlePrev(e);
        (e.keyCode === keyCode.ARROW_RIGHT) && this.handleNext(e);
    }

    handleSlideClick(e) {
        if (e && (
            (e.target === e.currentTarget) ||
            e.target.classList.contains('thumbnail') ||
            e.target.classList.contains('pinch-to-zoom-component')
        )) {
            this.closeModal();
        }
    }

    handleClose() {
        const { currSlideIndex } = this.state,
            { onClose } = this.props;

        onClose && onClose(currSlideIndex);

        if (window && window.document) {
            window.document.body.classList.remove('no-scroll');
        }
    }

    handleZoomActive(index) {
        return () => this.setState({slideZoomingIndex: index});
    }

    handleZoomIdle(index) {
        return () => this.setState((prevState) => {
            if (prevState.slideZoomingIndex === index) {
                return {slideZoomingIndex: null};
            }
        });
    }

    getModalHandles({open, close, isOpen}) {
        this.openModal = open;
        this.closeModal = close;
        this.isOpen = isOpen;
        return null;
    }

    renderSlide(slide, index) {
        // Turns AudioObject, ImageObject into <Slides.AudioSlide>, <Slides.ImageSlide>, etc.
        // eslint-disable-next-line import/namespace
        const SlideComponent = Slides[slide.itemScopeType],
            { currSlideIndex } = this.state,
            slideProps = {
                slide,
                key: `${index}--${slide.media.medium || slide.src}`,
                isActive: (index === currSlideIndex),
                expanded: true,
                onClick: this.handleSlideClick,
                onZoomActive: this.handleZoomActive(index),
                onZoomIdle: this.handleZoomIdle(index),

                // For now, this is pretty dumb "one ahead" preloader, if we need to make it more
                // complex we need to move this logic to it's own function.
                preload: (index > currSlideIndex && index <= currSlideIndex + LOAD_AHEAD)
            };

        return SlideComponent ? <SlideComponent {...slideProps}/> : null;
    }

    renderNavigation() {
        const { slides } = this.props,
            arrows = [
                (<a key="ss-prev" href="#" className="modal-nav-prev" onClick={this.handlePrev} rel="nofollow"/>),
                (<a key="ss-next" href="#" className="modal-nav-next" onClick={this.handleNext} rel="nofollow"/>)
            ];
        return (slides.length > 1) ? arrows : null;
    }

    renderContent({isOpen}) {
        // Avoid rendering unnecessary content.
        // The modal package complaints if no content (null) is passed as children, so we pass an empty span instead.
        if (!isOpen) { return <span/>; }

        const { slides, transition } = this.props,
            { currSlideIndex } = this.state,
            slidesComponents = slides.map(this.renderSlide);

        return (
            <div ref={(el) => (this.slideshowWrapper = el)} onKeyDown={this.handleKeyDown} tabIndex={-1}>
                <Swipeable onSwipedLeft={this.handleSwipeLeft}
                    onSwipedRight={this.handleSwipeRight}
                    className="swipe-events-wrapper">
                    <Slideshow selectedIndex={currSlideIndex} transition={transition}>
                        { slidesComponents }
                    </Slideshow>
                </Swipeable>
                { this.renderNavigation() }
                <span className="modal-close" onClick={this.closeModal}>×</span>
            </div>
        );
    }

    render() {
        return (
            <Base name="gallery-modal"
                onClose={this.handleClose}
                onOpen={this.handleOpen}
                trigger={this.getModalHandles}
                bindBodyClickListener={false}>
                { this.renderContent }
            </Base>
        );
    }
}

GalleryModal.propTypes = {
    slides: PropTypes.array.isRequired,
    selectedIndex: PropTypes.number,
    transition: PropTypes.string,
    onClose: PropTypes.func
};

GalleryModal.defaultProps = {
    selectedIndex: 0,
    transition: 'slide-h',
    onClose: null
};

GalleryModal.contextTypes = {
    biEvents: PropTypes.object
};

export default GalleryModal;
